import {
  AssessmentEntry,
  AssessmentMethod,
  AssessmentMethodType,
  AssessmentsGrade,
  AssessmentWithEntries,
  isAssessmentEntryComment,
  isAssessmentEntryGrade,
  isAssessmentEntryScore,
} from '@schooly/api';
import React, { FC } from 'react';

import { useMarkbookCellContext } from '../../../pages/Assessments/Markbook/common/MarkbookCellContext';
import { UpdateAssessmentEntryParams } from '../../../pages/Assessments/Markbook/common/types';
import { CommentController, CommentControllerProps } from './methods/CommentController';
import { GradeController, GradeControllerProps } from './methods/GradeController';
import { ScoreController, ScoreControllerProps } from './methods/ScoreController';

import './AssessmentEntryMethods.scss';

export interface AssessmentEntryMethodProps {
  relationId: string;
  groupId: string;
  method: AssessmentMethod;
  assessmentId: string;
  enterable: boolean;
  lists?: Record<string, AssessmentsGrade>;
  onSuccess?: () => void;
  commentControllerProps?: Partial<CommentControllerProps>;
  gradeControllerProps?: Partial<GradeControllerProps>;
  scoreControllerProps?: Partial<ScoreControllerProps>;
  updateAssessmentEntry?: (p: UpdateAssessmentEntryParams) => void;
  entries: AssessmentEntry[];
}

export function getAssessmentParams(
  relationId: string,
  method: AssessmentMethod,
  assessment?: AssessmentWithEntries,
) {
  const students = assessment?.groups[0]?.students ?? [];
  const relation = students.find((item) => item.relation_id === relationId);

  if (!method || !assessment || !relation) {
    return null;
  }

  const entries = relation.entries?.filter(
    (item) => item.method.id === method.id && item.method.type === method.method_type,
  );

  return { relation, entries };
}

export function hasAssessmentEntry(
  relationId: string,
  method: AssessmentMethod,
  assessment?: AssessmentWithEntries,
): boolean {
  return !!getAssessmentParams(relationId, method, assessment);
}

export const AssessmentEntryMethod: FC<AssessmentEntryMethodProps> = ({
  relationId,
  groupId,
  method,
  assessmentId,
  enterable,
  lists,
  onSuccess,
  commentControllerProps,
  gradeControllerProps,
  scoreControllerProps,
  updateAssessmentEntry,
  entries,
}) => {
  const { getAssessmentEntryColor } = useMarkbookCellContext();

  const entry = entries[0];

  switch (method.method_type) {
    case AssessmentMethodType.Score:
      return (
        <ScoreController
          key={`score-${relationId}-${groupId}`}
          relationId={relationId}
          groupId={groupId}
          entry={entry && isAssessmentEntryScore(entry) ? entry : undefined}
          method={method}
          assessmentId={assessmentId}
          enterable={enterable}
          onSuccess={onSuccess}
          updateAssessmentEntry={updateAssessmentEntry}
          {...scoreControllerProps}
        />
      );

    case AssessmentMethodType.Grade:
      return (
        <GradeController
          key={`grade-${relationId}-${groupId}`}
          relationId={relationId}
          groupId={groupId}
          entry={entry && isAssessmentEntryGrade(entry) ? entry : undefined}
          method={method}
          assessmentId={assessmentId}
          enterable={enterable}
          list={lists?.[method.select_list_id]}
          onSuccess={onSuccess}
          updateAssessmentEntry={updateAssessmentEntry}
          {...gradeControllerProps}
          getCellColor={getAssessmentEntryColor}
        />
      );

    case AssessmentMethodType.Comment:
      return (
        <CommentController
          key={`comment-${relationId}-${groupId}`}
          relationId={relationId}
          groupId={groupId}
          entries={entries.filter(isAssessmentEntryComment)}
          method={method}
          assessmentId={assessmentId}
          enterable={enterable}
          onSuccess={onSuccess}
          updateAssessmentEntry={updateAssessmentEntry}
          {...commentControllerProps}
        />
      );
  }
};
